import { useEffect, useMemo, useReducer } from 'react';
import { UserFetchParams } from 'services/users';
import {
  fromOrdering,
  toOrdering,
  getNextSortState,
  saveSortState,
  loadSortState,
} from 'utils/sorting';
import { SortStorageKeyParts } from 'types/sorting';

export interface UserTableFetchActions {
  type:
    | 'SET_STATUS'
    | 'SET_ROLE'
    | 'SET_PAGE'
    | 'SET_SEARCH'
    | 'SET_PAGE_SIZE'
    | 'SET_ORDERING'
    | 'SET_TEAM';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any;
}

export const getTeamUsersSortStorageKey = (
  teamSlug: string,
  active: string
): SortStorageKeyParts => ({
  table: 'team-users',
  variant: active === 'false' ? 'pending' : 'active',
  context: teamSlug,
});

export const isTeamUsersOrderingValidForStatus = (
  ordering: string | null | undefined,
  active: string
): boolean => {
  const currentKey = ordering?.replace(/^-/, '') || null;
  if (!currentKey) return true;
  const activeValidKeys = ['user_display', 'user__last_login'];
  const pendingValidKeys = ['user_display', 'invited_at'];
  const validKeys = active !== 'false' ? activeValidKeys : pendingValidKeys;
  return validKeys.includes(currentKey);
};

export const usersTableDataFetchReducer = (
  state: UserFetchParams,
  action: UserTableFetchActions
): UserFetchParams => {
  if (action.type === 'SET_STATUS') {
    const nextActive = typeof action.value === 'object' ? action.value.active : action.value;
    const requestedOrdering =
      typeof action.value === 'object' ? action.value.ordering : state.ordering;
    const newOrdering = isTeamUsersOrderingValidForStatus(requestedOrdering, nextActive)
      ? requestedOrdering
      : null;
    return { ...state, active: nextActive, page: 1, ordering: newOrdering };
  } else if (action.type === 'SET_ORDERING') {
    return { ...state, ordering: action.value, page: 1 };
  } else if (action.type === 'SET_TEAM') {
    return { ...state, teams: action.value.teamSlug, ordering: action.value.ordering, page: 1 };
  } else if (action.type === 'SET_PAGE_SIZE') {
    return { ...state, page_size: action.value, page: 1 };
  } else if (action.type === 'SET_PAGE') {
    return { ...state, page: action.value };
  } else if (action.type === 'SET_SEARCH') {
    return { ...state, search: action.value };
  } else {
    return state;
  }
};

interface TeamUserSearchHook {
  requestData: UserFetchParams;
  handlePagination: (page: string, name: string) => void;
  handleSearchboxInput: (value: string) => void;
  handleStatusFilter: (value: string) => void;
  handleSortSelect: (ordering: string) => void;
  handleSortToggle: (columnKey: string) => void;
}

const useTeamUsersSearch = ({
  teamSlug,
  sortingEnabled,
}: {
  teamSlug: string;
  sortingEnabled?: boolean;
}): TeamUserSearchHook => {
  const [requestData, setRequestData] = useReducer(usersTableDataFetchReducer, {
    active: 'true',
    license: null,
    teams: teamSlug,
    roles: null,
    page: 1,
    page_size: 6,
    search: null,
    ordering: null,
  });

  const storageKey = useMemo(
    () => getTeamUsersSortStorageKey(teamSlug, requestData.active),
    [teamSlug, requestData.active]
  );

  useEffect(() => {
    const savedOrdering = sortingEnabled
      ? toOrdering(loadSortState(getTeamUsersSortStorageKey(teamSlug, 'true')))
      : null;
    const ordering = isTeamUsersOrderingValidForStatus(savedOrdering, 'true')
      ? savedOrdering
      : null;
    setRequestData({ type: 'SET_TEAM', value: { teamSlug, ordering } });
  }, [teamSlug]);

  useEffect(() => {
    saveSortState(storageKey, fromOrdering(requestData.ordering));
  }, [requestData.ordering, storageKey]);

  const handlePagination = (page: string, name: string) => {
    if (name === 'page_size') {
      setRequestData({ type: 'SET_PAGE_SIZE', value: page });
    } else if (name === 'page') {
      setRequestData({ type: 'SET_PAGE', value: page });
    }
  };

  const handleSearchboxInput = (value: string) => {
    if (value !== requestData.search) {
      setRequestData({ type: 'SET_SEARCH', value });
    }
  };

  const handleStatusFilter = (value: string) => {
    const savedOrdering = sortingEnabled
      ? toOrdering(loadSortState(getTeamUsersSortStorageKey(teamSlug, value)))
      : null;
    const ordering = isTeamUsersOrderingValidForStatus(savedOrdering, value) ? savedOrdering : null;
    setRequestData({ type: 'SET_STATUS', value: { active: value, ordering } });
  };

  const handleSortToggle = (columnKey: string) => {
    const currentState = fromOrdering(requestData.ordering);
    const nextState = getNextSortState(currentState, columnKey);
    const nextOrdering = toOrdering(nextState);
    setRequestData({ type: 'SET_ORDERING', value: nextOrdering || null });
  };

  const handleSortSelect = (ordering: string) => {
    const nextOrdering = isTeamUsersOrderingValidForStatus(ordering || null, requestData.active)
      ? ordering || null
      : null;
    setRequestData({ type: 'SET_ORDERING', value: nextOrdering });
  };

  return {
    handlePagination,
    handleSearchboxInput,
    handleStatusFilter,
    handleSortSelect,
    handleSortToggle,
    requestData,
  };
};

export default useTeamUsersSearch;
